import React, { useRef, useState, useEffect } from 'react'
import './ExperienceSection.css'

const FORMATIONS = [
  {
    period: '2026 — 2028',
    title: 'Master Expert en science des données spécialisé en infrastructure data ou en apprentissage automatique ',
    school: 'Ynov Campus Nanterre',
    type: 'upcoming',
    desc: 'Spécialisation en intelligence artificielle, IoT et systèmes embarqués connectés.',
    tags: ['IA', 'DATA', 'Gestion projet'],
  },
  {
    period: '2025 — 2026',
    title: 'Bachelor 3 Intelligence Artificielle & Data',
    school: 'Ynov Campus Nanterre',
    type: 'current',
    desc: 'Formation intensive en data science, machine learning et développement IA.',
    tags: ['Python', 'Machine Learning', 'Data Analysis','Gestion de projet'],
  },
  {
    period: '2023 — 2025',
    title: 'BTS Système Informatique Organisation (SIO)',
    school: 'Lycée Jean Vilar, Plaisir',
    type: 'done',
    desc: 'Formation aux systèmes d\'information, développement logiciel et administration réseaux.',
    tags: ['Dev', 'Réseaux', 'Systèmes'],
  },
  {
    period: '2020 — 2023',
    title: 'Baccalauréat STMG — Spécialité SIG',
    school: 'Mention Assez Bien',
    type: 'done',
    desc: 'Systèmes d\'Information de Gestion : données, bases de données, algorithmique.',
    tags: ['SIG', 'Mention AB'],
  },
]

const EXPERIENCES = [
  {
    period: 'fév 2026 — juil 2026',
    title: 'Développeur Full-Stack — Auto-école',
    company: "Stage d'étude",
    type: 'current',
    desc: 'Développement complet du site full-stack avec hébergement. Création d\'un outil interne de gestion de flotte de véhicules. Base de données clients et IA chatbot spécifique ANTS / Permis.',
    tags: ['React', 'Full-Stack', 'IA Chatbot', 'PostgreSQL'],
  },
  {
    period: 'Depuis 2024',
    title: 'Développeur Web Freelance',
    company: 'Indépendant',
    type: 'current',
    desc: 'Création de sites web complets pour restaurants, taxis, auto-écoles, commerces et associations. Frontend responsive, SGBDR, MCD/MLD, hébergement OVH / Hostinger / Oracle.',
    tags: ['Vue.js', 'React', 'SQL', 'OVH', 'Hostinger'],
  },
  {
    period: 'Depuis 2024',
    title: 'Développeur Logiciels & Applications Python',
    company: 'Projets personnels',
    type: 'current',
    desc: 'Facturation (devis, factures, gestion et reporting). Réseau social de rassemblement automobile avec messagerie instantanée.',
    tags: ['Python', 'C#', 'Tkinter', 'SQLite'],
  },
  {
    period: 'mai 2024 — juil 2024',
    title: 'Optimisation Workflows — Stellantis',
    company: 'Stellantis (visio)',
    type: 'done',
    desc: 'Simplification des process de commande de matériel informatique. Création de la documentation des nouveaux process. Développement de logiciels et applications Python.',
    tags: ['Python', 'Analyse de process', 'Documentation'],
  },
  {
    period: '2023 — 2024',
    title: 'Service Civique',
    company: 'Plaisir Rugby Club',
    type: 'done',
    desc: 'Mission d\'engagement citoyen, gestion et support événementiel pour le club.',
    tags: ['Bénévolat', 'Événementiel'],
  },
]

function useReveal(threshold = 0.1) {
  const ref = useRef(null)
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); obs.disconnect() }
    }, { threshold })
    if (ref.current) obs.observe(ref.current)
    return () => obs.disconnect()
  }, [threshold])
  return [ref, visible]
}

function TimelineItem({ item, index, visible }) {
  const statusColor = item.type === 'current' ? 'var(--green)'
    : item.type === 'upcoming' ? 'var(--blue)'
    : 'var(--text-3)'

  return (
    <div
      className={`timeline-item ${item.type} ${visible ? 'revealed' : ''}`}
      style={{ '--delay': `${index * 100}ms` }}
    >
      <div className="timeline-dot" style={{ '--dot-color': statusColor }}>
        <div className="timeline-dot-inner" />
      </div>
      <div className="timeline-card glass-card">
        <div className="timeline-card-top">
          <span className="timeline-period">{item.period}</span>
          {item.type === 'current' && (
            <span className="timeline-badge timeline-badge-current">
              <span className="dot-pulse" style={{ '--pulse-color': 'var(--green)' }} />
              En cours
            </span>
          )}
          {item.type === 'upcoming' && (
            <span className="timeline-badge timeline-badge-upcoming">Prévu</span>
          )}
        </div>
        <h3 className="timeline-title">{item.title}</h3>
        <p className="timeline-company">{item.school || item.company}</p>
        <p className="timeline-desc">{item.desc}</p>
        <div className="timeline-tags">
          {item.tags.map(t => (
            <span key={t} className="timeline-tag">{t}</span>
          ))}
        </div>
      </div>
    </div>
  )
}

export default function ExperienceSection() {
  const [sectionRef, visible] = useReveal()
  const [tab, setTab] = useState('exp')

  return (
    <section id="experience" className="section experience-section" ref={sectionRef} aria-label="Parcours">
      <div className={`section-inner ${visible ? 'visible' : ''}`}>
        <div className="section-label"><span>04</span> Parcours</div>
        <h2 className="section-title">
          Formation &amp; <span className="gradient-text">Expériences</span>
        </h2>
        <p className="section-desc">Mon chemin académique et professionnel, des premières lignes de code aux projets en production.</p>

        {/* Tab switcher */}
        <div className="exp-tabs" role="tablist">
          <button
            className={`exp-tab ${tab === 'exp' ? 'active' : ''}`}
            onClick={() => setTab('exp')}
            role="tab"
            aria-selected={tab === 'exp'}
          >
            Expériences pro
          </button>
          <button
            className={`exp-tab ${tab === 'edu' ? 'active' : ''}`}
            onClick={() => setTab('edu')}
            role="tab"
            aria-selected={tab === 'edu'}
          >
            Formations
          </button>
        </div>

        {/* Timeline */}
        <div className="timeline" role="tabpanel">
          {(tab === 'exp' ? EXPERIENCES : FORMATIONS).map((item, i) => (
            <TimelineItem key={item.title} item={item} index={i} visible={visible} />
          ))}
        </div>
      </div>
    </section>
  )
}
